import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, TrendingUp } from 'lucide-react';
import { PlaceCard } from '../components/PlaceCard';
import { FilterBar } from '../components/FilterBar';
import { SearchBar } from '../components/SearchBar';
import { Footer } from '../components/Footer';
import { ScrollToTop } from '../components/ScrollToTop';
import { NairobiClock } from '../components/NairobiClock';
import { places, categories } from '../data/places';
import type { PlaceCategory } from '../data/places';
export function ExplorePage() {
  const [selectedCategories, setSelectedCategories] = useState<PlaceCategory[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [displayCount, setDisplayCount] = useState(20);
  // South Estates (existing featured)
  const southEstates = [{
    id: 'karen',
    name: 'Karen',
    character: 'Colonial Elegance & Refined Living',
    image: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&q=80'
  }, {
    id: 'lavington',
    name: 'Lavington',
    character: 'Established Prestige & Timeless Elegance',
    image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=80'
  }, {
    id: 'kileleshwa',
    name: 'Kileleshwa',
    character: 'Refined Residential Tranquility',
    image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=80'
  }, {
    id: 'spring-valley',
    name: 'Spring Valley',
    character: 'Serene Sophistication & Garden Living',
    image: 'https://images.unsplash.com/photo-1600585154363-67eb9e2e2099?w=800&q=80'
  }];
  // North Estates
  const northEstates = [{
    id: 'muthaiga',
    name: 'Muthaiga',
    character: 'Elite Exclusivity & Distinguished Heritage',
    image: 'https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=800&q=80'
  }, {
    id: 'runda',
    name: 'Runda',
    character: 'Gated Luxury & Modern Prestige',
    image: 'https://images.unsplash.com/photo-1600585154526-990dced4db0d?w=800&q=80'
  }, {
    id: 'gigiri',
    name: 'Gigiri',
    character: 'Diplomatic Quarter & International Prestige',
    image: 'https://images.unsplash.com/photo-1582407947304-fd86f028f716?w=800&q=80'
  }, {
    id: 'rosslyn',
    name: 'Rosslyn',
    character: 'Modern Luxury & Valley Views',
    image: 'https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?w=800&q=80'
  }];
  // Central/West Estates
  const centralEstates = [{
    id: 'westlands',
    name: 'Westlands',
    character: 'Cosmopolitan Energy & Modern Sophistication',
    image: 'https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?w=800&q=80'
  }, {
    id: 'kilimani',
    name: 'Kilimani',
    character: 'Contemporary Urban Elegance',
    image: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=800&q=80'
  }, {
    id: 'parklands',
    name: 'Parklands',
    character: 'Cultural Diversity & Urban Vibrancy',
    image: 'https://images.unsplash.com/photo-1600566752355-35792bedcfea?w=800&q=80'
  }];
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const handleCategoryToggle = (category: PlaceCategory) => {
    setSelectedCategories(prev => prev.includes(category) ? prev.filter(c => c !== category) : [...prev, category]);
  };
  const handleClearFilters = () => {
    setSelectedCategories([]);
    setSearchQuery('');
  };
  const filteredAndSortedPlaces = useMemo(() => {
    let filtered = places;
    if (selectedCategories.length > 0) {
      filtered = filtered.filter(place => place.categories.some(cat => selectedCategories.includes(cat)));
    }
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(place => place.name.toLowerCase().includes(query) || place.description.toLowerCase().includes(query) || place.location.toLowerCase().includes(query) || place.categories.some(cat => cat.toLowerCase().includes(query)));
    }
    return [...filtered].sort((a, b) => a.name.localeCompare(b.name));
  }, [selectedCategories, searchQuery]);
  const displayedPlaces = filteredAndSortedPlaces.slice(0, displayCount);
  const hasMore = displayCount < filteredAndSortedPlaces.length;
  return <div className="min-h-screen bg-white" data-id="element-941">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gray-900" data-id="element-942">
        <div className="absolute inset-0" data-id="element-943">
          <img src="https://images.unsplash.com/photo-1611348524140-53c9a25263d6?w=1920&q=80" alt="Nairobi skyline" className="w-full h-full object-cover opacity-40" data-id="element-944" />
        </div>

        <div className="relative max-w-7xl mx-auto px-8 lg:px-12 py-32 md:py-48" data-id="element-945">
          <motion.div initial={{
          y: 30,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          duration: 1,
          ease: [0.22, 1, 0.36, 1]
        }} className="max-w-4xl" data-id="element-946">
            <div className="mb-8" data-id="element-947">
              <div className="h-px w-16 bg-amber-500 mb-8" data-id="element-948"></div>
              <span className="text-xs text-amber-500 font-light uppercase tracking-[0.3em]" data-id="element-949">
                Discover Excellence
              </span>
            </div>

            <h1 className="text-6xl md:text-8xl font-light text-white mb-8 leading-[1.1] tracking-tight" data-id="element-950">
              Visit
              <br data-id="element-951" />
              <span className="font-poppins text-7xl md:text-9xl" data-id="element-952">Nairobi</span>
            </h1>

            <p className="text-xl md:text-2xl text-gray-300 leading-relaxed mb-16 max-w-2xl font-light" data-id="element-953">
              Experience Africa's most distinguished capital, where untamed
              nature and refined sophistication converge in perfect harmony.
            </p>

            <div className="flex flex-wrap gap-12" data-id="element-954">
              <div className="relative" data-id="element-955">
                <div className="text-5xl font-light text-white mb-2" data-id="element-956">
                  {places.length}
                </div>
                <p className="text-xs text-gray-400 uppercase tracking-widest font-light" data-id="element-957">
                  Curated Destinations
                </p>
                <div className="absolute -bottom-2 left-0 w-12 h-px bg-amber-500" data-id="element-958"></div>
              </div>

              <div className="relative" data-id="element-959">
                <div className="text-5xl font-light text-white mb-2" data-id="element-960">
                  {categories.length}
                </div>
                <p className="text-xs text-gray-400 uppercase tracking-widest font-light" data-id="element-961">
                  Exclusive Categories
                </p>
                <div className="absolute -bottom-2 left-0 w-12 h-px bg-amber-500" data-id="element-962"></div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Nairobi Clock */}
      <div className="max-w-7xl mx-auto px-8 lg:px-12 -mt-16 relative z-10" data-id="element-963">
        <motion.div initial={{
        y: 20,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} transition={{
        delay: 0.5
      }} className="max-w-md" data-id="element-964">
          <NairobiClock data-id="element-965" />
        </motion.div>
      </div>

      {/* Search Bar */}
      <div className="bg-white border-b border-gray-200 mt-16" data-id="element-966">
        <div className="max-w-7xl mx-auto px-8 lg:px-12 py-8" data-id="element-967">
          <div className="max-w-2xl" data-id="element-968">
            <SearchBar value={searchQuery} onChange={setSearchQuery} placeholder="Search destinations..." data-id="element-969" />
          </div>
        </div>
      </div>

      {/* Filter Section */}
      <div className="bg-gray-50 border-b border-gray-200" data-id="element-970">
        <div className="max-w-7xl mx-auto px-8 lg:px-12 py-12" data-id="element-971">
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="mb-8" data-id="element-972">
            <div className="h-px w-16 bg-amber-500 mb-6" data-id="element-973"></div>
            <h2 className="text-3xl font-light text-gray-900 mb-3 tracking-tight" data-id="element-974">
              Curated Collections
            </h2>
            <p className="text-gray-600 font-light" data-id="element-975">
              Select your preferred experiences
            </p>
          </motion.div>
        </div>
      </div>

      <FilterBar selectedCategories={selectedCategories} onCategoryToggle={handleCategoryToggle} onClearFilters={handleClearFilters} data-id="element-976" />

      {/* Results Count */}
      <div className="max-w-7xl mx-auto px-8 lg:px-12 mt-12" data-id="element-977">
        <motion.p className="text-sm text-gray-500 font-light tracking-wider uppercase" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} key={filteredAndSortedPlaces.length} data-id="element-978">
          {filteredAndSortedPlaces.length}{' '}
          {filteredAndSortedPlaces.length === 1 ? 'Destination' : 'Destinations'}
          {selectedCategories.length > 0 && <span data-id="element-979"> · {selectedCategories.join(', ')}</span>}
        </motion.p>
      </div>

      {/* Places Grid - 4 Columns */}
      <div className="max-w-7xl mx-auto px-8 lg:px-12 py-12" data-id="element-980">
        {filteredAndSortedPlaces.length > 0 ? <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12" data-id="element-981">
              {displayedPlaces.map((place, index) => <PlaceCard key={place.id} place={place} index={index} data-id="element-982" />)}
            </div>

            {/* Load More Button */}
            {hasMore && <div className="text-center mb-20" data-id="element-983">
                <button onClick={() => setDisplayCount(prev => prev + 20)} className="px-10 py-4 border-2 border-gray-900 text-gray-900 font-light hover:bg-gray-900 hover:text-white transition-colors text-sm uppercase tracking-widest" data-id="element-984">
                  Load More Destinations
                </button>
              </div>}

            {/* South Estates Section - WARM COLORS */}
            <motion.div className="my-32 bg-gradient-to-br from-orange-500 to-red-600 text-white overflow-hidden" initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-985">
              <div className="p-16 md:p-24" data-id="element-986">
                <div className="max-w-4xl mx-auto text-center mb-16" data-id="element-987">
                  <div className="h-px w-16 bg-white/50 mx-auto mb-8" data-id="element-988"></div>
                  <span className="text-xs text-white/80 font-light uppercase tracking-[0.3em] block mb-8" data-id="element-989">
                    South Nairobi
                  </span>
                  <h2 className="text-5xl md:text-6xl font-light mb-8 tracking-tight" data-id="element-990">
                    Colonial Heritage
                    <br data-id="element-991" />
                    <span className="font-serif italic" data-id="element-992">& Garden Estates</span>
                  </h2>
                  <p className="text-xl text-white/90 leading-relaxed font-light" data-id="element-993">
                    Where history meets refined living in Nairobi's most
                    prestigious southern suburbs.
                  </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16" data-id="element-994">
                  {southEstates.map((estate, index) => <motion.div key={estate.id} initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: 0.05 * index
              }} className="group" data-id="element-995">
                      <Link to={`/neighborhood/${estate.id}`} onClick={() => window.scrollTo(0, 0)} data-id="element-996">
                        <div className="relative aspect-[3/4] overflow-hidden mb-6 border-4 border-white/20" data-id="element-997">
                          <img src={estate.image} alt={estate.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" data-id="element-998" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" data-id="element-999" />
                          <div className="absolute bottom-0 left-0 right-0 p-6" data-id="element-1000">
                            <div className="h-px w-8 bg-white mb-4" data-id="element-1001"></div>
                            <h3 className="text-2xl font-light text-white mb-2" data-id="element-1002">
                              {estate.name}
                            </h3>
                            <p className="text-sm text-white/90 font-light" data-id="element-1003">
                              {estate.character}
                            </p>
                          </div>
                        </div>
                      </Link>
                    </motion.div>)}
                </div>
              </div>
            </motion.div>

            {/* North Estates Section - COOL COLORS */}
            <motion.div className="my-32 bg-gradient-to-br from-blue-600 to-purple-700 text-white overflow-hidden" initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-1004">
              <div className="p-16 md:p-24" data-id="element-1005">
                <div className="max-w-4xl mx-auto text-center mb-16" data-id="element-1006">
                  <div className="h-px w-16 bg-white/50 mx-auto mb-8" data-id="element-1007"></div>
                  <span className="text-xs text-white/80 font-light uppercase tracking-[0.3em] block mb-8" data-id="element-1008">
                    North Nairobi
                  </span>
                  <h2 className="text-5xl md:text-6xl font-light mb-8 tracking-tight" data-id="element-1009">
                    Elite Enclaves
                    <br data-id="element-1010" />
                    <span className="font-serif italic" data-id="element-1011">
                      & Diplomatic Quarter
                    </span>
                  </h2>
                  <p className="text-xl text-white/90 leading-relaxed font-light" data-id="element-1012">
                    Exclusive gated communities and international prestige in
                    Nairobi's northern heights.
                  </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16" data-id="element-1013">
                  {northEstates.map((estate, index) => <motion.div key={estate.id} initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: 0.05 * index
              }} className="group" data-id="element-1014">
                      <Link to={`/neighborhood/${estate.id}`} onClick={() => window.scrollTo(0, 0)} data-id="element-1015">
                        <div className="relative aspect-[3/4] overflow-hidden mb-6 border-4 border-white/20" data-id="element-1016">
                          <img src={estate.image} alt={estate.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" data-id="element-1017" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" data-id="element-1018" />
                          <div className="absolute bottom-0 left-0 right-0 p-6" data-id="element-1019">
                            <div className="h-px w-8 bg-white mb-4" data-id="element-1020"></div>
                            <h3 className="text-2xl font-light text-white mb-2" data-id="element-1021">
                              {estate.name}
                            </h3>
                            <p className="text-sm text-white/90 font-light" data-id="element-1022">
                              {estate.character}
                            </p>
                          </div>
                        </div>
                      </Link>
                    </motion.div>)}
                </div>
              </div>
            </motion.div>

            {/* Central/West Estates Section - GREEN COLORS */}
            <motion.div className="my-32 bg-gradient-to-br from-teal-600 to-green-700 text-white overflow-hidden" initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-1023">
              <div className="p-16 md:p-24" data-id="element-1024">
                <div className="max-w-4xl mx-auto text-center mb-16" data-id="element-1025">
                  <div className="h-px w-16 bg-white/50 mx-auto mb-8" data-id="element-1026"></div>
                  <span className="text-xs text-white/80 font-light uppercase tracking-[0.3em] block mb-8" data-id="element-1027">
                    Central & West Nairobi
                  </span>
                  <h2 className="text-5xl md:text-6xl font-light mb-8 tracking-tight" data-id="element-1028">
                    Urban Sophistication
                    <br data-id="element-1029" />
                    <span className="font-serif italic" data-id="element-1030">& Modern Living</span>
                  </h2>
                  <p className="text-xl text-white/90 leading-relaxed font-light" data-id="element-1031">
                    Cosmopolitan energy and contemporary elegance in the heart
                    of the city.
                  </p>
                </div>

                <div className="grid md:grid-cols-3 gap-8 mb-16 max-w-5xl mx-auto" data-id="element-1032">
                  {centralEstates.map((estate, index) => <motion.div key={estate.id} initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: 0.05 * index
              }} className="group" data-id="element-1033">
                      <Link to={`/neighborhood/${estate.id}`} onClick={() => window.scrollTo(0, 0)} data-id="element-1034">
                        <div className="relative aspect-[3/4] overflow-hidden mb-6 border-4 border-white/20" data-id="element-1035">
                          <img src={estate.image} alt={estate.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" data-id="element-1036" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" data-id="element-1037" />
                          <div className="absolute bottom-0 left-0 right-0 p-6" data-id="element-1038">
                            <div className="h-px w-8 bg-white mb-4" data-id="element-1039"></div>
                            <h3 className="text-2xl font-light text-white mb-2" data-id="element-1040">
                              {estate.name}
                            </h3>
                            <p className="text-sm text-white/90 font-light" data-id="element-1041">
                              {estate.character}
                            </p>
                          </div>
                        </div>
                      </Link>
                    </motion.div>)}
                </div>

                <div className="text-center" data-id="element-1042">
                  <Link to="/neighborhoods" onClick={() => window.scrollTo(0, 0)} className="inline-flex items-center gap-3 px-10 py-4 bg-white text-gray-900 font-light hover:bg-gray-100 transition-colors text-sm uppercase tracking-widest" data-id="element-1043">
                    Explore All Estates
                    <ArrowRight className="w-4 h-4" strokeWidth={1.5} data-id="element-1044" />
                  </Link>
                </div>
              </div>
            </motion.div>

            {/* CTA Section - KEEP IMAGE */}
            <motion.div className="my-32 relative overflow-hidden" initial={{
          opacity: 0,
          y: 40
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-1045">
              <div className="relative" data-id="element-1046">
                <img src="https://images.unsplash.com/photo-1516426122078-c23e76319801?w=1920&q=80" alt="Nairobi National Park" className="w-full h-[500px] object-cover" data-id="element-1047" />
                <div className="absolute inset-0 bg-black/60" data-id="element-1048" />
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-1049">
                  <div className="text-center px-6 max-w-3xl" data-id="element-1050">
                    <motion.div initial={{
                  opacity: 0,
                  y: 20
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  delay: 0.2
                }} data-id="element-1051">
                      <div className="h-px w-16 bg-amber-500 mx-auto mb-8" data-id="element-1052"></div>
                      <TrendingUp className="w-10 h-10 text-white mx-auto mb-8" strokeWidth={1.5} data-id="element-1053" />
                      <h2 className="text-5xl font-light text-white mb-8 tracking-tight" data-id="element-1054">
                        Plan Your
                        <br data-id="element-1055" />
                        <span className="font-serif italic" data-id="element-1056">
                          Distinguished Visit
                        </span>
                      </h2>
                      <p className="text-xl text-gray-300 mb-12 leading-relaxed font-light" data-id="element-1057">
                        Discover essential travel insights and curated
                        recommendations for an exceptional Nairobi experience.
                      </p>
                      <Link to="/plan" onClick={() => window.scrollTo(0, 0)} className="inline-flex items-center gap-3 px-10 py-4 bg-white text-gray-900 font-light hover:bg-gray-100 transition-colors text-sm uppercase tracking-widest" data-id="element-1058">
                        Begin Planning
                        <ArrowRight className="w-4 h-4" strokeWidth={1.5} data-id="element-1059" />
                      </Link>
                    </motion.div>
                  </div>
                </div>
              </div>
            </motion.div>
          </> : <motion.div className="text-center py-32 bg-white border border-gray-200" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} data-id="element-1060">
            <p className="text-4xl font-light text-gray-400 mb-6" data-id="element-1061">
              No destinations found
            </p>
            <p className="text-gray-600 mb-8 font-light" data-id="element-1062">
              Refine your search criteria
            </p>
            <button onClick={handleClearFilters} className="px-8 py-3 border border-gray-900 text-gray-900 font-light hover:bg-gray-900 hover:text-white transition-colors text-sm uppercase tracking-widest" data-id="element-1063">
              Clear Filters
            </button>
          </motion.div>}
      </div>

      <ScrollToTop data-id="element-1064" />
      <Footer data-id="element-1065" />
    </div>;
}